import React, {
  MutableRefObject,
  useEffect,
  useRef,
  useState,
  forwardRef,
} from "react";
import classNames from "classnames";
import {
  computeOffset,
  computeValueBasedOnDragPosition,
  decrementValue,
  formatNumber,
  getNumberOfDecimalPlaces,
  getOffset,
  getSliderWidth,
  incrementValue,
  updateValuesOnBlur,
} from "./slider.util";
import {
  MouseOrTouchEvent,
  ReactElementOrNull,
  ReactMouseOrTouchEvent,
  Slider as SliderInterface,
  SliderType,
} from "./slider.types";
import uniqueId from "lodash.uniqueid";
import { TextInputBase } from "../text-input/text-input-base.jsx";
import {
  showSSRHydrationWarning,
  isPropEmpty,
} from "../utils/SSR/showSSRHydrationWarning";
import { useSSR } from "../utils/SSR/useSSR";

export interface SliderWithForwardRef
  extends React.ForwardRefExoticComponent<
    SliderInterface & React.RefAttributes<HTMLInputElement>
  > {}

export const Slider = forwardRef<HTMLInputElement, SliderInterface>(
  (
    {
      value: valueProp,
      defaultValue,
      minValue,
      maxValue,
      step = "1",
      label,
      disabled = false,
      sliderType = SliderType.DEFAULT,
      alternate = false,
      onChange,
      className,
      name: sliderNameProp,
      labelId: labelIdProp,
      ...rest
    },
    ref
  ): ReactElementOrNull => {
    // Initialize value of the slider from props & keep it updated
    const defaultValueComputed =
      defaultValue !== undefined ? defaultValue : minValue;
    const valueComputed: number =
      valueProp !== undefined ? valueProp : defaultValueComputed;
    const [value, setValue] = useState(valueComputed);
    const [numberOfDecimalPlaces, setNumberOfDecimalPlaces] = useState(
      getNumberOfDecimalPlaces(step)
    );
    const [stepValue, setStepValue] = useState(Number(step));
    const { current: labelUniqueId } = useRef(uniqueId("spark-label_"));
    const { current: sliderUniqueName } = useRef(
      uniqueId("slider-name-auto-generated_")
    );

    useSSR(
      () =>
        isPropEmpty(labelIdProp) && showSSRHydrationWarning("labelId", "Slider")
    );
    const labelId = labelIdProp || labelUniqueId;
    const name = sliderNameProp || sliderUniqueName;

    useEffect(() => {
      setNumberOfDecimalPlaces(getNumberOfDecimalPlaces(step));
      setStepValue(Number(step));
    }, [step]);

    useEffect(() => {
      setValue(Math.round(valueComputed / stepValue) * stepValue);
    }, [stepValue, valueComputed]);

    // Used to track the mouse interactions and apply styles
    const [active, setActive] = useState(false);

    // Holds the value of the input field
    const [inputValue, setInputValue] = useState<string>(
      valueComputed.toFixed(numberOfDecimalPlaces)
    );

    // Compute the offset for button on load and set it. Passed callback for useState for performance reasons
    const [buttonOffset, setButtonOffset] = useState("0%");

    // Compute the trackWidth on load. Passed callback for useState for performance reasons
    const [trackWidth, setTrackWidth] = useState(0);

    // Updates the state hooks with the same value, invoke onChange callback when found
    const updateValue = (value: number): void => {
      const newValue = Math.round(value / stepValue) * stepValue;
      setValue(newValue);
      setInputValue(newValue.toFixed(numberOfDecimalPlaces));
      onChange?.(newValue);
    };

    useEffect(() => {
      const newValue = Math.round(value / stepValue) * stepValue;
      setInputValue(newValue.toFixed(numberOfDecimalPlaces));
    }, [numberOfDecimalPlaces, stepValue, value]);

    // Recalculate offset for button and track when the value, minValue, maxValue changes
    useEffect(() => {
      const sliderWidth = getSliderWidth(sliderEl.current);
      const { buttonOffset, trackOffset } = computeOffset({
        minValue,
        maxValue,
        value,
        isButton: true,
        sliderType,
        ceilValue: maxValue,
        floorValue: minValue,
        sliderWidth,
        stepValue,
        buttonWidth: buttonEl.current.offsetWidth,
      });
      setButtonOffset(buttonOffset);
      setTrackWidth(trackOffset);
    }, [maxValue, minValue, value, sliderType, stepValue]);

    // Resize slider on window resize
    const [, setResizeTS] = useState(0);
    const onResize = (e: Event): void => setResizeTS(e.timeStamp);
    useEffect(() => {
      window.addEventListener("resize", onResize);
      return () => {
        window.removeEventListener("resize", onResize);
      };
    });

    // Ref for track component to get details from dom like width
    const trackEl = useRef<HTMLSpanElement>(null);
    const sliderEl = useRef<HTMLDivElement>(
      null
    ) as MutableRefObject<HTMLDivElement>;
    const buttonEl = useRef<HTMLButtonElement>(
      null
    ) as MutableRefObject<HTMLButtonElement>;

    // General error messages when possible error props occur
    if (
      value === undefined ||
      minValue === undefined ||
      maxValue === undefined ||
      step === undefined
    ) {
      console.error(
        "At least one of the slider required props do not have a value, slider won't render. Check value, minValue, maxValue & step prop in <Slider/>."
      );
      return null;
    }
    if (minValue >= maxValue) {
      console.error("minValue must be less than maxValue");
    }
    if (value < minValue) {
      console.error("value must be greater than minValue");
    }
    if (value > maxValue) {
      console.error("value must be lesser than maxValue");
    }

    /*
     * Handle keystrokes on the button on focus
     * 1. up or right will increment until the maxValue
     * 2. down or left will decrement until the minValue
     */
    const changeSliderValueOnKeyDown = ({ key }: React.KeyboardEvent): void => {
      if (disabled) {
        return;
      }
      if (key === "ArrowRight" || key === "ArrowUp") {
        updateValue(
          incrementValue({
            value,
            step: stepValue,
            maxValue,
            decimalsLength: numberOfDecimalPlaces,
          })
        );
      }

      if (key === "ArrowLeft" || key === "ArrowDown") {
        updateValue(
          decrementValue({
            minValue,
            value,
            step: stepValue,
            decimalsLength: numberOfDecimalPlaces,
          })
        );
      }
    };

    // Mouse is moved on the slider. The track fill and button position to be computed
    const onMouseMove = (e: MouseOrTouchEvent): void => {
      const sliderObject = getOffset(sliderEl.current);
      if (typeof sliderObject === "boolean") {
        return;
      }
      const dragPosition =
        ("clientX" in e ? e.clientX : e.touches[0].clientX) - sliderObject.left;

      // The event occurred in between min and max value range.
      const newValue = computeValueBasedOnDragPosition({
        dragPosition,
        sliderWidth: sliderObject.width,
        minValue,
        maxValue,
        step: stepValue,
        decimalsInStep: numberOfDecimalPlaces,
      });
      updateValue(newValue);
    };

    // User has finished dragging. Triggered when mouse leaves the slider controls
    const onMouseUp = (): void => {
      if (disabled) {
        return;
      }
      setActive(false);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mousemove", onMouseMove);

      window.removeEventListener("touchend", onMouseUp);
      window.removeEventListener("touchmove", onMouseMove);
    };

    // Triggered on mouse down event on the button. User is about to drag
    const changeSliderValue = (e: ReactMouseOrTouchEvent): void => {
      buttonEl.current.focus();
      if (disabled) {
        return;
      }
      e.cancelable && e.preventDefault();
      setActive(true);

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);

      window.addEventListener("touchmove", onMouseMove);
      window.addEventListener("touchend", onMouseUp);
    };

    // Compute the position of button and track fill width when slider is clicked/touched
    const onSliderTouch = (e: React.MouseEvent): void => {
      if (disabled) {
        return;
      }
      e.preventDefault();
      const sliderObject = getOffset(sliderEl.current);
      const btnObject = getOffset(buttonEl.current);
      if (typeof sliderObject === "boolean" || typeof btnObject === "boolean") {
        return;
      }
      const positionClicked = e.clientX - sliderObject.left;

      const newValue = computeValueBasedOnDragPosition({
        dragPosition: positionClicked,
        sliderWidth: sliderObject.width,
        minValue,
        maxValue,
        step: stepValue,
        decimalsInStep: numberOfDecimalPlaces,
      });
      updateValue(newValue);
    };

    // Handles the keyboard events that can occur in the input
    const inputKeyHandler = (e: React.KeyboardEvent): void => {
      if (disabled) {
        return;
      }
      switch (e.key) {
        case "ArrowUp":
          arrowUpHandler();
          break;
        case "ArrowDown":
          arrowDownHandler();
          break;
        case "Enter":
          updateValuesOnBlur({
            updaterFn: updateValue,
            disabled,
            minValue,
            maxValue,
            valueFromInput: inputValue,
          });
      }
      return;
    };

    // triggered when up arrow is pressed in the input
    const arrowUpHandler = (): void => {
      if (value + stepValue >= maxValue) {
        updateValue(maxValue);
        return;
      }
      const newValue = Number(
        (value + stepValue).toFixed(numberOfDecimalPlaces)
      );
      updateValue(newValue);
    };

    // triggered when down arrow is pressed in the input
    const arrowDownHandler = (): void => {
      if (value - stepValue <= minValue) {
        updateValue(minValue);
        return;
      }
      const newValue = Number(
        (value - stepValue).toFixed(numberOfDecimalPlaces)
      );
      updateValue(newValue);
    };

    const sliderClassNames = classNames(
      {
        "spark-slider": sliderType === SliderType.DEFAULT,
        "spark-slider--input": sliderType === SliderType.INPUT,
        "spark-slider--integrated": sliderType === SliderType.INTEGRATED,
        active: sliderType !== SliderType.DEFAULT && active,
        "spark-slider--secondary": alternate,
      },
      className
    );

    const buttonClassNames = classNames("spark-slider__handle", {
      active: sliderType !== SliderType.DEFAULT && active,
    });

    return (
      <div className={sliderClassNames} {...rest}>
        <label className="spark-label" id={labelId} htmlFor={name}>
          {label}
        </label>
        <TextInputBase
          type="number"
          ref={ref}
          min={minValue}
          max={maxValue}
          value={inputValue}
          step={step}
          name={name}
          id={name}
          disabled={disabled}
          onKeyDown={inputKeyHandler}
          onChange={(e): void => setInputValue(e.target.value)}
          onBlur={(): void =>
            updateValuesOnBlur({
              updaterFn: updateValue,
              disabled,
              minValue,
              maxValue,
              valueFromInput: inputValue,
            })
          }
          isSlider={true}
        />
        <div
          className="spark-slider__controls"
          ref={sliderEl}
          onMouseLeave={(): void => {
            onMouseUp();
          }}
        >
          <button
            type="button"
            className={buttonClassNames}
            role="slider"
            ref={buttonEl}
            aria-controls={name}
            aria-orientation="horizontal"
            aria-valuemin={minValue}
            aria-valuemax={maxValue}
            aria-valuenow={value}
            aria-valuetext={value.toString()}
            aria-labelledby={labelId}
            data-value={
              sliderType === SliderType.INTEGRATED
                ? formatNumber(value, numberOfDecimalPlaces)
                : value
            }
            tabIndex={disabled ? -1 : undefined}
            style={{ left: buttonOffset }}
            onTouchStart={changeSliderValue}
            onMouseDown={changeSliderValue}
            onKeyDown={(e): void => changeSliderValueOnKeyDown(e)}
          />
          <span
            className="spark-slider__track"
            data-percentage={trackWidth}
            ref={trackEl}
            onMouseDown={onSliderTouch}
          >
            <span
              className="spark-slider__track-fill"
              style={{ width: `${trackWidth}%` }}
            />
          </span>
        </div>
      </div>
    );
  }
) as SliderWithForwardRef;

Slider.displayName = "Slider";
